import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import type { Locale } from "date-fns";
import { UserX, CalendarDays, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { LeaveRequest } from "@/lib/api";
import { useDateLocale } from "@/hooks/useDateLocale";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AvailabilityEntry {
  employeeId: number;
  employeeName: string;
  leaveTypeLabel: string | null;
  leaveTypeColor: string | null;
  startDate: string;
  endDate: string;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface TeamAvailabilityIndicatorProps {
  requests: LeaveRequest[];
  isLoading: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toDay(s: string): Date {
  const d = parseISO(s);
  d.setHours(0, 0, 0, 0);
  return d;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TeamAvailabilityIndicator({
  requests,
  isLoading,
}: TeamAvailabilityIndicatorProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const { offToday, laterThisWeek, upcoming } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    // Week boundaries (Mon–Sun)
    const weekStart = new Date(today);
    const dayOfWeek = weekStart.getDay();
    const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    weekStart.setDate(weekStart.getDate() + diffToMon);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const twoWeeksOut = new Date(today);
    twoWeeksOut.setDate(twoWeeksOut.getDate() + 14);
    twoWeeksOut.setHours(23, 59, 59, 999);

    const offToday: AvailabilityEntry[] = [];
    const laterThisWeek: AvailabilityEntry[] = [];
    const upcoming: AvailabilityEntry[] = [];

    for (const r of requests) {
      if (r.status !== "Approved" && r.status !== "Pending") continue;
      const start = toDay(r.startDate);
      const end = toDay(r.endDate);
      end.setHours(23, 59, 59, 999);

      const entry: AvailabilityEntry = {
        employeeId: r.employeeId,
        employeeName: r.employeeName,
        leaveTypeLabel: r.leaveTypeLabel,
        leaveTypeColor: r.leaveTypeColor,
        startDate: r.startDate,
        endDate: r.endDate,
      };

      // Off today: approved and today is within leave range
      if (
        r.status === "Approved" &&
        start.getTime() <= todayEnd.getTime() &&
        end.getTime() >= today.getTime()
      ) {
        offToday.push(entry);
      }
      // Later this week: starts after today but before/on Sunday
      else if (
        start.getTime() > todayEnd.getTime() &&
        start.getTime() <= weekEnd.getTime()
      ) {
        laterThisWeek.push(entry);
      }
      // Upcoming: starts after this week and within 14 days
      else if (
        start.getTime() > weekEnd.getTime() &&
        start.getTime() <= twoWeeksOut.getTime()
      ) {
        upcoming.push(entry);
      }
    }

    // Sort upcoming by start date
    laterThisWeek.sort(
      (a, b) => toDay(a.startDate).getTime() - toDay(b.startDate).getTime(),
    );
    upcoming.sort(
      (a, b) => toDay(a.startDate).getTime() - toDay(b.startDate).getTime(),
    );

    return { offToday, laterThisWeek, upcoming };
  }, [requests]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Off Today */}
      <AvailabilitySection
        icon={UserX}
        title={t("leave.offToday")}
        description={t("leave.offTodayDesc")}
        colorClass="text-red-600"
        bgClass="bg-red-500/10"
        isLoading={isLoading}
        entries={offToday}
        emptyMessage={t("leave.noOneOffToday")}
        showDate={false}
        dateLocale={dateLocale}
      />

      {/* Later This Week */}
      <AvailabilitySection
        icon={CalendarDays}
        title={t("leave.offThisWeek")}
        description={t("leave.offThisWeekDesc")}
        colorClass="text-blue-600"
        bgClass="bg-blue-500/10"
        isLoading={isLoading}
        entries={laterThisWeek}
        emptyMessage={t("leave.noOneOffThisWeek")}
        showDate
        dateLocale={dateLocale}
      />

      {/* Upcoming */}
      <AvailabilitySection
        icon={Clock}
        title={t("leave.upcomingAbsences")}
        description={t("leave.upcomingAbsencesDesc")}
        colorClass="text-violet-600"
        bgClass="bg-violet-500/10"
        isLoading={isLoading}
        entries={upcoming}
        emptyMessage={t("leave.noUpcomingAbsences")}
        showDate
        dateLocale={dateLocale}
      />
    </div>
  );
}

// ── Sub-component ─────────────────────────────────────────────────────────────

function AvailabilitySection({
  icon: Icon,
  title,
  description,
  colorClass,
  bgClass,
  isLoading,
  entries,
  emptyMessage,
  showDate,
  dateLocale,
}: {
  icon: typeof UserX;
  title: string;
  description: string;
  colorClass: string;
  bgClass: string;
  isLoading: boolean;
  entries: AvailabilityEntry[];
  emptyMessage: string;
  showDate: boolean;
  dateLocale: Locale;
}) {
  const { t } = useTranslation();
  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${bgClass}`}>
            <Icon className={`h-3.5 w-3.5 ${colorClass}`} />
          </div>
          {title}
          {!isLoading && entries.length > 0 && (
            <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1 text-xs font-bold text-muted-foreground">
              {entries.length}
            </span>
          )}
        </CardTitle>
        <p className="mt-1 text-xs leading-snug text-muted-foreground">
          {description}
        </p>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-9 rounded-md" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">{emptyMessage}</p>
        ) : (
          <ul className="space-y-1.5">
            {entries.map((e, idx) => (
              <li
                key={`${e.employeeId}-${e.startDate}-${idx}`}
                className="flex items-center gap-2 text-sm"
              >
                {/* Avatar */}
                <div className="h-7 w-7 rounded-full gradient-brand flex items-center justify-center text-primary-foreground text-[10px] font-semibold shrink-0">
                  {e.employeeName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground truncate">
                    {e.employeeName}
                  </p>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    {e.leaveTypeColor && (
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: e.leaveTypeColor }}
                      />
                    )}
                    <span className="truncate">
                      {e.leaveTypeLabel ?? t("leave.leaveType")}
                    </span>
                    {showDate && (
                      <>
                        <span>·</span>
                        <span>{format(parseISO(e.startDate), "d MMM", { locale: dateLocale })}</span>
                        {e.startDate !== e.endDate && (
                          <span>-{format(parseISO(e.endDate), "d MMM", { locale: dateLocale })}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
